import { ImageCoordinates } from '../types';

export class FocusAreaCropInitializer {
	private readonly resizeObserver = new ResizeObserver((entries) => {
		entries.forEach((entry) => this.applyFocusArea(entry.target as HTMLImageElement));
	});

	public init() {
		this.applyFocusAreas();
		this.ajaxListeners();
	}

	/**
	 * Percentage for one `object-position` axis that puts the middle of the focus area in the
	 * middle of the section `object-fit: cover` actually shows.
	 *
	 * `visible` is the size of that section in image coordinates - it is not the size of the
	 * focus area. Using the focus area instead makes the crop jump as soon as the focus area is
	 * not perfectly centered, because a focus area that covers almost the whole image leaves a
	 * denominator of only a few pixels and blows up that small offset.
	 */
	public offsetPercent(start: number, size: number, total: number, visible: number): number {
		const overflow = total - visible;

		// nothing is cropped away on this axis, so the position has no effect anyway
		if (overflow <= 0) {
			return 50;
		}

		const offset = start + size / 2 - visible / 2;

		return Math.min(Math.max((offset / overflow) * 100, 0), 100);
	}

	private applyFocusAreas() {
		const focusArea = document.querySelectorAll<HTMLImageElement>('[data-focus-area]');

		focusArea.forEach((image) => {
			this.applyFocusArea(image);

			// the visible section depends on the rendered size, so it has to follow the layout
			this.resizeObserver.observe(image);
		});
	}

	private applyFocusArea(image: HTMLImageElement) {
		const focusArea: ImageCoordinates = JSON.parse(image.dataset.focusArea!);

		const width = Number.parseInt(image.getAttribute('width')!);
		const height = Number.parseInt(image.getAttribute('height')!);

		const boxWidth = image.clientWidth;
		const boxHeight = image.clientHeight;

		if (!width || !height || !boxWidth || !boxHeight) {
			return;
		}

		// Only a cover image hides part of itself, everything else is shown completely and
		// would just be pushed around inside its box.
		if (getComputedStyle(image).objectFit !== 'cover') {
			image.style.removeProperty('object-position');

			return;
		}

		// section of the image that `object-fit: cover` leaves visible, in image coordinates.
		// Cover always keeps one axis complete and crops the other one.
		const boxRatio = boxWidth / boxHeight;
		const cropsHeight = boxRatio > width / height;

		const visibleWidth = cropsHeight ? width : height * boxRatio;
		const visibleHeight = cropsHeight ? width / boxRatio : height;

		const xPercent = this.offsetPercent(focusArea.x, focusArea.width, width, visibleWidth).toFixed(2);
		const yPercent = this.offsetPercent(focusArea.y, focusArea.height, height, visibleHeight).toFixed(2);

		image.style.objectPosition = `${xPercent}% ${yPercent}%`;
	}

	private ajaxListeners() {
		const container = document.querySelector('.searchresult-container');

		container?.addEventListener('eventLoad', () => {
			this.applyFocusAreas();
		});
	}
}
